/**
 * Case Studies — Enterprise customer success stories
 *
 * Used by /casos and /casos/:slug routes.
 * Content is Spanish-first because case studies target LATAM enterprise.
 */

export interface CaseStudy {
	slug: string;
	name: string;
	industry: string;
	title: string;
	metaTitle: string;
	metaDescription: string;
	heroSubtitle: string;
	beforeProvider: string;
	afterProvider: string;
	savingsPercent?: number;
	stats: { label: string; value: string }[];
	problem: string[];
	solution: string[];
	results: string[];
	quote?: { text: string; author: string; role: string };
	stack: { label: string; value: string }[];
	ctaText: string;
	// Expanded narrative content for SEO and sales
	company?: string;
	challenge?: string;
	challengeDetails?: string[];
	approach?: string;
	approachDetails?: string[];
	outcomes?: string[];
	faq?: { question: string; answer: string }[];
}

export const CASE_STUDIES: CaseStudy[] = [
	{
		slug: 'klopp',
		name: 'Klopp',
		industry: 'Plataforma tecnológica',
		title: 'Cómo Klopp redujo 40% su factura cloud migrando de AWS y Google Cloud',
		metaTitle: 'Klopp redujo 40% sus costos cloud con Deployalo | Caso de éxito',
		metaDescription:
			'Klopp operaba Kubernetes, Kafka y microservicios en AWS + Google Cloud. Con Deployalo Managed redujeron 40% sus costos y ahora escalan sin miedo.',
		heroSubtitle:
			'Klopp operaba una arquitectura robusta con Kubernetes, Kafka, múltiples bases de datos y decenas de microservicios. Su factura en AWS y Google Cloud crecía cada mes, y escalar cualquier componente significaba una sorpresa en la siguiente factura.',
		beforeProvider: 'AWS + Google Cloud',
		afterProvider: 'Deployalo Managed',
		savingsPercent: 40,
		stats: [
			{ label: 'Reducción de costos', value: '40%' },
			{ label: 'Arquitectura', value: 'Kubernetes, 3 nodos' },
			{ label: 'Soporte', value: '24/7 en español' },
		],
		problem: [
			'Facturas impredecibles: cada escalón de tráfico se traducía en una sorpresa en la factura.',
			'Servicios cloud costosos que realmente no aportaban valor a su caso.',
			'Miedo a escalar: el equipo pensaba dos veces antes de subir recursos.',
		],
		solution: [
			'VPS dedicados con recursos garantizados.',
			'Kubernetes operado y monitoreado 24/7.',
			'Backups automáticos y snapshots.',
			'Soporte humano + Deply (IA) en español.',
			'Sin vendor lock-in: arquitectura portable.',
		],
		results: [
			'40% de reducción en costos de infraestructura.',
			'Escalamiento predecible: saben cuánto cuesta crecer antes de hacerlo.',
			'El equipo volvió a enfocarse en el producto.',
			'Cuando necesitaron más capacidad, agregaron un tercer nodo al cluster sin renegociar contrato ni migrar nada: precio fijo conocido desde el día uno.',
			'Infraestructura más simple, sin servicios de cloud innecesarios.',
		],
		quote: {
			text: 'Con Deployalo sabemos exacto cuánto vamos a pagar. Eso cambia completamente cómo planeamos el crecimiento.',
			author: 'Equipo Klopp',
			role: 'Tecnología',
		},
		stack: [
			{ label: 'Orquestación', value: 'Kubernetes' },
			{ label: 'Streaming', value: 'Kafka' },
			{ label: 'Bases de datos', value: 'Múltiples DBs' },
			{ label: 'Infraestructura', value: 'VPS dedicados gestionados' },
			{ label: 'Soporte', value: '24/7 en español + Deply AI' },
		],
		ctaText: '¿Tu arquitectura en AWS/GCP te está costando más de lo necesario?',
		company:
			'Klopp es una plataforma tecnológica de alto rendimiento que procesa grandes volúmenes de datos en tiempo real. Su producto depende de una arquitectura distribuida con microservicios, streaming de eventos y múltiples bases de datos especializadas. Antes de Deployalo, toda esta infraestructura corría en AWS y Google Cloud, lo que les daba flexibilidad pero a un costo creciente y difícil de predecir.',
		challenge:
			'La arquitectura de Klopp estaba diseñada para escalar, pero su factura cloud escalaba más rápido que su negocio. Cada nuevo microservicio, base de datos o cola de mensajes significaba más servicios administrados, más transferencia de datos y más sorpresas al final del mes.',
		challengeDetails: [
			'Facturas impredecibles: los costos de egress, storage y servicios administrados crecían mes a mes sin una relación clara con el tráfico real.',
			'Over-engineering de cloud: AWS y GCP ofrecían docenas de servicios, pero Klopp solo necesitaba computo confiable, Kubernetes y buenas bases de datos.',
			'Miedo a escalar: cada vez que el equipo planeaba crecer, primero calculaba el impacto en la factura cloud. Eso ralentizaba decisiones de producto.',
			'Falta de soporte en español: cuando había incidentes fuera de horario, los tickets en inglés aumentaban el tiempo de resolución.',
		],
		approach:
			'Deployalo diseñó una infraestructura dedicada y portable. Migraron los workloads de Klopp a VPS con recursos garantizados, mantuvieron Kubernetes para orquestación y conectaron Kafka para el streaming de eventos. Todo bajo monitoreo 24/7 y con Deply AI como primera línea de diagnóstico.',
		approachDetails: [
			'Auditoría de costos previa: identificamos qué servicios de AWS/GCP realmente aportaban valor y cuáles podían reemplazarse por infraestructura dedicada.',
			'Kubernetes gestionado sobre VPS dedicados, con autoscaling horizontal controlado y sin costos sorpresa por egress.',
			'Kafka y bases de datos operadas por Deployalo, con backups automáticos, snapshots y monitoreo de salud.',
			'Soporte humano en español disponible 24/7, con Deply AI acelerando el diagnóstico de incidentes.',
			'Migración sin downtime, validando cada componente antes de cortar tráfico.',
		],
		outcomes: [
			'40% de reducción en costos totales de infraestructura en el primer trimestre.',
			'Facturación predecible: ahora saben cuánto cuesta agregar un nodo antes de hacerlo.',
			'Tiempo de respuesta a incidentes reducido gracias a Deply AI y soporte en español.',
			'El equipo de ingeniería recuperó horas semanales de operación cloud para enfocarse en el producto.',
			'Arquitectura portable sin vendor lock-in: pueden mover workloads entre proveedores en días, no meses.',
		],
		faq: [
			{
				question: '¿Se puede reducir 40% en costos cloud sin perder rendimiento?',
				answer:
					'Sí. En el caso de Klopp, la mayor parte del gasto venía de servicios administrados y egress que no aportaban valor directo. Al mover los workloads a VPS dedicados con Kubernetes gestionado, mantuvieron el mismo rendimiento y ganaron previsibilidad.',
			},
			{
				question: '¿Cuánto tiempo toma migrar una arquitectura con Kubernetes y Kafka?',
				answer:
					'Depende de la complejidad, pero la mayoría de las migraciones enterprise se completan en 2 a 4 semanas. En Klopp hicimos la migración por fases para validar cada componente y evitar downtime.',
			},
			{
				question: '¿Deployalo solo funciona para empresas que usan AWS?',
				answer:
					'No. Migramos desde AWS, Google Cloud, Azure, DigitalOcean, Hetzner y otras nubes. La estrategia es siempre la misma: entender el workload, eliminar lo innecesario y operar infraestructura dedicada.',
			},
		],
	},
	{
		slug: 'paymoney',
		name: 'PayMoney',
		industry: 'Fintech / tarjetas de vale',
		title: 'Cómo PayMoney eliminó el vendor lock-in y unificó su infraestructura',
		metaTitle: 'PayMoney unificó su infraestructura con Deployalo | Caso de éxito',
		metaDescription:
			'PayMoney maneja tarjetas de vale y estaba en AWS + DigitalOcean. Con Deployalo ganó control, simplicidad y cero vendor lock-in.',
		heroSubtitle:
			'PayMoney opera tarjetas de vale y movimientos de dinero. Su infraestructura estaba dividida: producción en AWS y ambientes de desarrollo en DigitalOcean. Eso les generaba doble factura, doble complejidad y dependencia de proveedores.',
		beforeProvider: 'AWS + DigitalOcean',
		afterProvider: 'Deployalo Managed',
		stats: [
			{ label: 'Proveedores antes', value: '2' },
			{ label: 'Vendor lock-in', value: '0' },
			{ label: 'Administración', value: 'Unificada' },
		],
		problem: [
			'Infraestructura fragmentada: producción en AWS, desarrollo en DigitalOcean.',
			'Vendor lock-in: cada vez que usaban más servicios de AWS, era más difícil salir.',
			'Complejidad operativa: dos proveedores, dos facturas, dos formas de trabajar.',
		],
		solution: [
			'VPS dedicados para producción y desarrollo.',
			'Monitoreo 24/7 y backups.',
			'Soporte humano + Deply (IA) en español.',
			'Arquitectura portable.',
			'Una sola factura y un solo punto de contacto.',
		],
		results: [
			'Infraestructura unificada bajo un solo proveedor.',
			'0 vendor lock-in: la arquitectura es portable.',
			'Más fácil de administrar.',
			'Soporte en español disponible 24/7.',
		],
		quote: {
			text: 'Ahora entendemos nuestra infraestructura. No dependemos de servicios que no controlamos, y si tenemos un problema a las 2 AM nos responden en español.',
			author: 'Equipo PayMoney',
			role: 'Operaciones',
		},
		stack: [
			{ label: 'Plataforma', value: 'Tarjetas de vale / fintech' },
			{ label: 'Infraestructura', value: 'VPS dedicados gestionados' },
			{ label: 'Ambientes', value: 'Producción + desarrollo unificados' },
			{ label: 'Soporte', value: '24/7 en español + Deply AI' },
		],
		ctaText: '¿Tu infraestructura está repartida entre varios proveedores?',
		company:
			'PayMoney es una fintech que administra tarjetas de vale digital y procesa movimientos de dinero para empresas y empleados. Por la naturaleza de su negocio, necesitan cumplir con estándares de seguridad, tener ambientes estables y poder escalar durante campañas y días de pago.',
		challenge:
			'PayMoney tenía la producción en AWS y los ambientes de desarrollo en DigitalOcean. Esa división generaba facturas duplicadas, procesos operativos distintos y una dependencia creciente de servicios propietarios que encarecían la salida de AWS.',
		challengeDetails: [
			'Infraestructura fragmentada: producción y desarrollo vivían en nubes distintas, con configuraciones diferentes y equipos trabajando con herramientas desiguales.',
			'Vendor lock-in en AWS: RDS, S3, Lambda y otros servicios administrados hacían cada vez más difícil migrar sin reescribir partes del sistema.',
			'Doble factura y doble complejidad: pagaban a dos proveedores y necesitaban conocimiento interno de ambos.',
			'Soporte en inglés y tickets lentos para resolver incidentes críticos fuera de horario.',
		],
		approach:
			'Deployalo unificó toda la infraestructura de PayMoney bajo VPS dedicados gestionados. Producción y desarrollo comparten la misma plataforma, las mismas herramientas de monitoreo y el mismo equipo de soporte. La arquitectura se diseñó para ser portable y evitar servicios propietarios innecesarios.',
		approachDetails: [
			'Migración de producción desde AWS a VPS dedicados con equivalencia de servicios (bases de datos, storage, colas).',
			'Unificación de desarrollo y producción en una sola cuenta y plataforma de Deployalo.',
			'Monitoreo 24/7, backups automatizados y disaster recovery configurado.',
			'Soporte humano en español 24/7 más Deply AI para diagnóstico rápido.',
			'Eliminación progresiva de dependencias de servicios propietarios de AWS.',
		],
		outcomes: [
			'Infraestructura unificada bajo un solo proveedor y una sola factura mensual.',
			'0 vendor lock-in: la arquitectura es portable a otros proveedores en semanas.',
			'Reducción de complejidad operativa: el equipo ya no administra dos nubes.',
			'Soporte en español disponible 24/7, con tiempos de respuesta más rápidos.',
			'Mayor control sobre costos y recursos, especialmente en picos de uso.',
		],
		faq: [
			{
				question: '¿Cómo se elimina el vendor lock-in sin reescribir todo?',
				answer:
					'No es necesario reescribir todo. En PayMoney identificamos qué servicios administrados de AWS podían reemplazarse por alternativas open source o estándares, y migramos por fases. Kubernetes, bases de datos populares y storage S3-compatible son la base de una arquitectura portable.',
			},
			{
				question: '¿Es seguro mover una fintech de AWS a VPS dedicados?',
				answer:
					'Sí, siempre que se mantengan las mismas prácticas de seguridad: cifrado, backups, monitoreo, actualizaciones y control de acceso. Deployalo implementa estos estándares por defecto en todos los entornos enterprise.',
			},
			{
				question: '¿Se puede unificar producción y desarrollo en un solo lugar?',
				answer:
					'Sí. PayMoney tenía producción en AWS y desarrollo en DigitalOcean. Los unificamos en Deployalo, lo que simplificó despliegues, monitoreo y soporte.',
			},
		],
	},
	{
		slug: 'relo',
		name: 'Relo',
		industry: 'Mobile Measurement Partner (MMP)',
		title: 'Cómo Relo maneja millones de eventos por hora con Deployalo',
		metaTitle: 'Relo procesa millones de eventos/hora con Deployalo | Caso de éxito',
		metaDescription:
			'Relo es una plataforma MMP que mueve millones de eventos por hora. Migró a Deployalo por costos, soporte y flexibilidad.',
		heroSubtitle:
			'Relo es una plataforma MMP (Mobile Measurement Partner) que procesa millones de eventos por hora. Escala rápido, genera tráfico masivo y no puede permitirse fallos ni sorpresas de facturación.',
		beforeProvider: 'Infraestructura cloud tradicional',
		afterProvider: 'Deployalo Managed',
		stats: [
			{ label: 'Escala', value: 'Millones de eventos/hora' },
			{ label: 'Control', value: 'Total a nivel servidor' },
			{ label: 'Soporte', value: '24/7 + Deply AI' },
		],
		problem: [
			'Alta demanda de infraestructura con tráfico variable.',
			'Costos crecientes a mayor escala.',
			'Dependencia de especialistas certificados y caros.',
			'Necesidad de soporte real, no tickets en inglés.',
		],
		solution: [
			'VPS dedicados de alto rendimiento.',
			'Monitoreo y alertas 24/7.',
			'Backups y disaster recovery.',
			'Deply (IA) para diagnóstico y resolución automática.',
			'Soporte humano en español.',
			'Menos dependencia de un equipo interno de cloud.',
		],
		results: [
			'Reducción de costos de infraestructura.',
			'Mayor control directo sobre sus servidores.',
			'Menos carga para el equipo de soporte interno.',
			'Soporte en español disponible 24/7.',
			'Deply resuelve problemas antes de que escaleen.',
		],
		quote: {
			text: 'Deply nos ha salvado varias veces. Antes necesitábamos a alguien revisando logs a las 3 AM; ahora la IA detecta y resuelve mucho antes de que nos demos cuenta.',
			author: 'Equipo Relo',
			role: 'Infraestructura',
		},
		stack: [
			{ label: 'Plataforma', value: 'MMP (Mobile Measurement Partner)' },
			{ label: 'Tráfico', value: 'Millones de eventos por hora' },
			{ label: 'Infraestructura', value: 'VPS dedicados gestionados' },
			{ label: 'Soporte/ops', value: 'Deply AI + soporte humano 24/7' },
		],
		ctaText: '¿Tu plataforma mueve tanta escala que la factura cloud te asusta?',
		company:
			'Relo es una plataforma de Mobile Measurement Partner (MMP) que procesa y atribuye millones de eventos móviles por hora. Sus clientes dependen de datos en tiempo real para optimizar campañas publicitarias, lo que exige una infraestructura de baja latencia, alta disponibilidad y capaz de escalar rápidamente sin fallos.',
		challenge:
			'Relo maneja picos masivos de tráfico generados por campañas publicitarias. En cloud tradicional, cada pico se traducía en costos exponenciales y la operación dependía de especialistas caros disponibles 24/7.',
		challengeDetails: [
			'Tráfico altamente variable: millones de eventos por hora en picos, con caídas rápidas después. Cloud tradicional cobra por uso, lo que hace imposible predecir la factura.',
			'Costos que crecen con la escala: más eventos significaban más servicios administrados, más almacenamiento y más egress.',
			'Dependencia de especialistas certificados: el equipo necesitaba gente con certificaciones cloud caras solo para operar la infraestructura.',
			'Necesidad de soporte real: incidentes a cualquier hora requieren respuesta inmediata, no tickets en inglés con SLA de horas.',
		],
		approach:
			'Deployalo diseñó una infraestructura de alto rendimiento con VPS dedicados, monitoreo en tiempo real y Deply AI como primera línea de operaciones. La arquitectura permite escalar horizontalmente para picos y reducir costos en periodos de baja demanda, todo sin depender de servicios propietarios.',
		approachDetails: [
			'VPS dedicados de alto rendimiento para ingestión y procesamiento de eventos, con recursos garantizados.',
			'Arquitectura escalable que permite agregar capacidad antes de campañas grandes y reducir después.',
			'Monitoreo y alertas 24/7 con umbrales personalizados para latencia, colas y uso de recursos.',
			'Deply AI revisando logs y métricas continuamente para detectar anomalías antes de que impacten.',
			'Soporte humano en español disponible 24/7 para escalar cuando la IA identifica algo crítico.',
			'Backups y disaster recovery para mantener la disponibilidad incluso en escenarios extremos.',
		],
		outcomes: [
			'Reducción significativa de costos de infraestructura al eliminar servicios administrados innecesarios.',
			'Mayor control directo sobre servidores: el equipo puede ajustar recursos sin depender de especialistas certificados.',
			'Menos carga para el equipo de soporte interno gracias a Deply AI.',
			'Soporte en español 24/7, con tiempos de respuesta mucho menores que con tickets en inglés.',
			'Capacidad de escalar para picos de campañas sin sorpresas de facturación.',
		],
		faq: [
			{
				question: '¿Se puede manejar tráfico masivo con VPS dedicados?',
				answer:
					'Sí. Relo procesa millones de eventos por hora con VPS dedicados de alto rendimiento. La clave es una arquitectura escalable, monitoreo en tiempo real y planificación de capacidad antes de picos.',
			},
			{
				question: '¿Cómo funciona Deply AI en producción?',
				answer:
					'Deply AI monitorea logs y métricas, detecta patrones anómalos y sugiere o aplica correcciones automáticas. Cuando un problema requiere intervención humana, escala al equipo de soporte de Deployalo con contexto completo.',
			},
			{
				question: '¿Deployalo reemplaza a mi equipo de DevOps?',
				answer:
					'No necesariamente. En Relo redujimos la carga operativa del equipo interno, pero no los reemplazamos. Deployalo se convierte en el socio de infraestructura 24/7 y el equipo interno se enfoca en el producto.',
			},
		],
	},
	{
		slug: 'sindicato-padron-digital',
		name: 'Sindicato nacional',
		industry: 'Sector sindical / servicios',
		title: 'Cómo un sindicato nacional digitalizó su padrón con OCR de IA y respaldos gestionados',
		metaTitle: 'Padrón sindical digital con IA — Caso de éxito Deployalo Managed',
		metaDescription:
			'Un sindicato nacional modernizó el registro de sus miles de afiliados: padrón digital web, OCR con IA para actas escaneadas y respaldos diarios gestionados. Todo operado por Deployalo.',
		heroSubtitle:
			'Miles de expedientes de afiliados vivían en papel y actas escaneadas. Digitalizarlos a mano hubiera tomado meses de captura manual. Con Deployalo, el sindicato hoy tiene un padrón digital consultable en línea, con OCR de IA que lee las actas automáticamente y respaldos que se verifican solos.',
		beforeProvider: 'Papel + captura manual',
		afterProvider: 'Deployalo Managed',
		stats: [
			{ label: 'Expedientes digitalizados', value: '+6,700' },
			{ label: 'Respaldos', value: 'Diarios, verificados' },
			{ label: 'Soporte', value: '24/7 en español' },
		],
		problem: [
			'Miles de expedientes de afiliados solo en papel y actas escaneadas, sin forma de consultarlos en línea.',
			'La captura manual hubiera costado meses de trabajo administrativo.',
			'Sin respaldos confiables: un incendio o una computadora dañada hubiera significado perder el padrón completo.',
			'Las cotizaciones de software empresarial pedían presupuestos fuera de su alcance.',
		],
		solution: [
			'Plataforma web propia del padrón, desplegada en la nube con su propio dominio.',
			'OCR con IA (a través del AI Gateway de Deployalo) que lee actas escaneadas y extrae los datos automáticamente.',
			'Respaldos diarios gestionados con doble copia y verificación automática de que realmente se pueden restaurar.',
			'Monitoreo 24/7 y soporte en español: el sindicato no necesita personal técnico.',
		],
		results: [
			'+6,700 expedientes digitalizados y consultables en línea.',
			'El OCR de IA procesa las actas en segundos en lugar de minutos de captura manual por documento.',
			'Respaldos verificados automáticamente: el padrón ya no depende de una sola computadora.',
			'Costo mensual fijo y predecible, una fracción de las cotizaciones de software empresarial.',
		],
		stack: [
			{ label: 'Plataforma', value: 'Padrón digital web con dominio propio' },
			{ label: 'IA', value: 'OCR con Claude vía AI Gateway Deployalo' },
			{ label: 'Base de datos', value: 'PostgreSQL gestionada' },
			{ label: 'Respaldos', value: 'Diarios, doble copia, verificación automática' },
			{ label: 'Operación', value: 'Deployalo Managed 24/7' },
		],
		ctaText: '¿Tu organización tiene información valiosa atrapada en papel o sistemas viejos?',
		company:
			'Un sindicato nacional del sector alimenticio y del comercio que representa a miles de trabajadores afiliados en todo el país. Su operación depende del padrón de afiliados: altas, bajas, cambios y trámites que durante décadas se manejaron en papel. Cualquier consulta significaba buscar físicamente el expediente.',
		challenge:
			'El padrón completo del sindicato existía solo en papel y actas escaneadas. Digitalizarlo a mano habría tomado meses de captura, y las soluciones empresariales que cotizaron pedían presupuestos de software y consultoría muy por encima de lo que una organización sindical puede pagar. Mientras tanto, el riesgo era real: el padrón entero dependía de que nada le pasara al archivo físico.',
		challengeDetails: [
			'Captura manual inviable: miles de actas y formatos, cada uno con datos que alguien tendría que teclear uno por uno.',
			'Riesgo de pérdida total: sin respaldos digitales, un siniestro en el archivo físico borraba décadas de historia laboral de los afiliados.',
			'Presupuesto limitado: las cotizaciones de software empresarial y consultoría superaban por mucho lo que el sindicato podía destinar.',
			'Sin personal técnico: la solución tenía que operarse sola, sin contratar programadores ni administradores de sistemas.',
		],
		approach:
			'Deployalo construyó y opera la plataforma completa: un sistema web de padrón digital con dominio propio, donde el personal del sindicato da de alta y consulta afiliados. Las actas escaneadas se procesan con OCR de IA a través del AI Gateway de Deployalo, que extrae los datos automáticamente usando modelos de última generación. Los respaldos corren diario de forma automática, con doble copia y verificación de restauración.',
		approachDetails: [
			'Plataforma web del padrón desplegada en infraestructura moderna (Cloudflare + PostgreSQL), con dominio propio y acceso controlado para el personal autorizado.',
			'OCR con IA vía AI Gateway: las actas escaneadas se envían a modelos Claude que leen y estructuran los datos, con validación humana solo en los casos dudosos.',
			'Respaldos diarios gestionados con copia doble (servidor + nube) y un verificador automático que confirma cada semana que los respaldos realmente se pueden restaurar.',
			'Precio fijo mensual todo incluido: plataforma, IA, respaldos, monitoreo y soporte en español. Sin sorpresas ni costos por usuario.',
		],
		outcomes: [
			'+6,700 expedientes digitalizados: el padrón completo consultable en línea desde cualquier lugar.',
			'Actas procesadas por IA en segundos: lo que costaba minutos de captura manual por documento ahora es automático.',
			'Riesgo eliminado: respaldos diarios verificados con doble copia, el padrón ya no depende del archivo físico.',
			'Costo predecible: una fracción de las cotizaciones empresariales, con factura mexicana (CFDI).',
		],
		faq: [
			{
				question: '¿La IA puede leer documentos escaneados viejos o en mal estado?',
				answer:
					'Sí. Los modelos de visión actuales (como Claude, que usamos vía AI Gateway) leen actas escaneadas, formularios a máquina de escribir e incluso manuscritos claros. Los documentos dudosos se marcan para validación humana en lugar de adivinar.',
			},
			{
				question: '¿Qué tan seguros están los datos del padrón?',
				answer:
					'La base de datos vive en infraestructura dedicada con acceso restringido, y los respaldos corren a diario con doble copia en ubicaciones separadas. Además, un verificador automático confirma semanalmente que los respaldos se pueden restaurar de verdad, no solo que "existen".',
			},
			{
				question: '¿Mi organización necesita personal técnico para algo así?',
				answer:
					'No. Ese es exactamente el punto del servicio managed: Deployalo construye, opera, respalda y actualiza todo. El personal del sindicato solo usa la plataforma; nosotros somos su departamento de sistemas.',
			},
		],
	},
];

export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
	return CASE_STUDIES.find((c) => c.slug === slug);
}

export function getAllCaseStudies(): CaseStudy[] {
	return CASE_STUDIES;
}
